import { useState, useEffect, useMemo } from "react";
import { collection, getDocs, query, orderBy, where, Timestamp } from "firebase/firestore";
import { db } from "../firebase/config";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0E1117; --surface: #161B27; --surface2: #1C2333; --surface3: #212A3A;
    --border: #252D3D; --accent: #F5A623; --accent-dim: rgba(245,166,35,0.10);
    --blue: #3B82F6; --blue-dim: rgba(59,130,246,0.10); --green: #22C55E; --green-dim: rgba(34,197,94,0.10);
    --red: #EF4444; --red-dim: rgba(239,68,68,0.12); --purple: #A855F7; --purple-dim: rgba(168,85,247,0.10);
    --text: #F1F5F9; --text-sub: #94A3B8; --text-muted: #475569;
    --best: #F5A623; --better: #3B82F6; --good: #22C55E;
  }
  body { background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; min-height: 100vh; }
  .header { display: flex; align-items: center; justify-content: space-between; padding: 0 32px; height: 64px; background: rgba(22,27,39,0.97); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 100; }
  .logo { display: flex; align-items: center; gap: 10px; }
  .logo-icon { width: 32px; height: 32px; background: var(--accent); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-family: 'Bebas Neue', sans-serif; font-size: 18px; color: #000; }
  .logo-text { font-family: 'Bebas Neue', sans-serif; font-size: 24px; letter-spacing: 3px; }
  .logo-text span { color: var(--accent); }
  .header-badge { background: var(--purple-dim); border: 1px solid rgba(168,85,247,0.3); border-radius: 20px; padding: 4px 12px; font-size: 11px; font-weight: 700; color: var(--purple); letter-spacing: 1px; text-transform: uppercase; }
  .header-right { display: flex; align-items: center; gap: 14px; }
  .header-store { font-size: 12px; color: var(--text-muted); }
  .header-store strong { color: var(--text-sub); }
  .avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--purple-dim); border: 1.5px solid var(--purple); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: var(--purple); }
  .layout { display: grid; grid-template-columns: 220px 1fr; min-height: calc(100vh - 64px); }
  .sidebar { background: var(--surface); border-right: 1px solid var(--border); padding: 24px 0; }
  .sidebar-label { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 20px; margin: 16px 0 8px; }
  .nav-item { display: flex; align-items: center; gap: 10px; padding: 9px 20px; font-size: 13px; font-weight: 500; color: var(--text-sub); cursor: pointer; transition: all 0.15s; border-left: 3px solid transparent; }
  .nav-item:hover { background: var(--surface2); color: var(--text); }
  .nav-item.active { background: var(--purple-dim); color: var(--purple); border-left-color: var(--purple); font-weight: 600; }
  .nav-icon { font-size: 15px; width: 20px; text-align: center; }
  .main { padding: 32px 36px; overflow-y: auto; }
  .filters-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 28px; gap: 12px; flex-wrap: wrap; }
  .filters-left { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .page-title { font-family: 'Bebas Neue', sans-serif; font-size: 32px; letter-spacing: 3px; line-height: 1; margin-right: 6px; }
  .range-pill { padding: 7px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.15s; border: 1px solid var(--border); color: var(--text-muted); background: transparent; font-family: 'DM Sans', sans-serif; }
  .range-pill.active { background: var(--purple-dim); color: var(--purple); border-color: rgba(168,85,247,0.4); }
  .range-pill:hover:not(.active) { border-color: var(--text-muted); color: var(--text-sub); }
  .advisor-select { background: var(--surface); border: 1px solid var(--border); border-radius: 8px; padding: 8px 12px; font-size: 13px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; cursor: pointer; }
  .advisor-select:focus { border-color: var(--purple); }
  .export-btn { padding: 8px 16px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: 'DM Sans', sans-serif; border: 1px solid var(--border); color: var(--text-sub); background: transparent; display: inline-flex; align-items: center; gap: 6px; transition: all 0.15s; }
  .export-btn:hover { border-color: var(--text-sub); color: var(--text); }
  .kpi-strip { display: grid; grid-template-columns: repeat(5, 1fr); gap: 14px; margin-bottom: 24px; }
  .kpi-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 18px 20px; position: relative; overflow: hidden; }
  .kpi-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; }
  .kpi-card.purple::before { background: var(--purple); } .kpi-card.amber::before { background: var(--accent); }
  .kpi-card.blue::before { background: var(--blue); } .kpi-card.green::before { background: var(--green); } .kpi-card.red::before { background: var(--red); }
  .kpi-label { font-size: 10px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }
  .kpi-value { font-family: 'Bebas Neue', sans-serif; font-size: 36px; letter-spacing: 1px; line-height: 1; }
  .kpi-value.purple { color: var(--purple); } .kpi-value.amber { color: var(--accent); } .kpi-value.blue { color: var(--blue); } .kpi-value.green { color: var(--green); }
  .kpi-sub { font-size: 11px; color: var(--text-muted); margin-top: 4px; }
  .chart-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 18px; }
  .chart-grid-3 { display: grid; grid-template-columns: 1.2fr 1fr 1fr; gap: 18px; margin-bottom: 18px; }
  .chart-card { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 22px; animation: fadeUp 0.3s ease both; }
  .chart-card.full { grid-column: 1 / -1; }
  .chart-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 4px; display: flex; align-items: center; justify-content: space-between; }
  .chart-subtitle { font-size: 12px; color: var(--text-muted); margin-bottom: 20px; }
  .chart-title-bar { display: flex; align-items: center; gap: 8px; }
  .chart-title-bar::before { content: ''; display: block; width: 3px; height: 12px; border-radius: 2px; }
  .chart-title-bar.purple::before { background: var(--purple); } .chart-title-bar.amber::before { background: var(--accent); }
  .chart-title-bar.blue::before { background: var(--blue); } .chart-title-bar.green::before { background: var(--green); }
  .tier-visual { display: flex; align-items: center; gap: 28px; margin-top: 10px; }
  .donut-wrap { position: relative; width: 130px; height: 130px; flex-shrink: 0; }
  .donut-center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; }
  .donut-center-val { font-family: 'Bebas Neue', sans-serif; font-size: 28px; letter-spacing: 1px; line-height: 1; color: var(--text); }
  .donut-center-label { font-size: 10px; color: var(--text-muted); margin-top: 2px; }
  .tier-legend { flex: 1; display: flex; flex-direction: column; gap: 14px; }
  .tier-legend-row { display: flex; flex-direction: column; gap: 5px; }
  .tier-legend-top { display: flex; align-items: center; justify-content: space-between; }
  .tier-legend-label { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: var(--text-sub); }
  .tier-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .tier-legend-pct { font-family: 'Bebas Neue', sans-serif; font-size: 20px; letter-spacing: 1px; }
  .tier-bar { height: 5px; background: var(--border); border-radius: 3px; overflow: hidden; }
  .tier-bar-fill { height: 100%; border-radius: 3px; transition: width 0.6s ease; }
  .bar-chart { display: flex; flex-direction: column; gap: 12px; margin-top: 10px; }
  .bar-row { display: flex; align-items: center; gap: 12px; }
  .bar-label { font-size: 12px; font-weight: 600; color: var(--text-sub); width: 100px; flex-shrink: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .bar-track { flex: 1; height: 28px; background: var(--surface2); border-radius: 6px; overflow: hidden; position: relative; }
  .bar-fill { height: 100%; border-radius: 6px; display: flex; align-items: center; padding: 0 10px; transition: width 0.6s ease; }
  .bar-fill-val { font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.9); white-space: nowrap; }
  .bar-trend { width: 42px; text-align: right; flex-shrink: 0; font-size: 11px; font-weight: 700; }
  .bar-trend.up { color: var(--green); } .bar-trend.down { color: var(--red); }
  .sparkline-wrap { margin-top: 12px; }
  .sparkline-days { display: flex; align-items: flex-end; gap: 6px; height: 80px; }
  .spark-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 5px; }
  .spark-bar-wrap { flex: 1; display: flex; align-items: flex-end; width: 100%; }
  .spark-bar { width: 100%; border-radius: 4px 4px 0 0; transition: height 0.5s ease; cursor: pointer; }
  .spark-bar:hover { filter: brightness(1.2); }
  .spark-label { font-size: 10px; color: var(--text-muted); font-weight: 600; }
  .spark-val { font-family: 'JetBrains Mono', monospace; font-size: 10px; color: var(--text-sub); }
  .sparkline-legend { display: flex; gap: 16px; margin-top: 14px; flex-wrap: wrap; }
  .spark-legend-item { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--text-muted); }
  .spark-legend-dot { width: 8px; height: 8px; border-radius: 2px; }
  .cat-table { display: flex; flex-direction: column; gap: 0; margin-top: 10px; }
  .cat-row { display: grid; grid-template-columns: 1.6fr 80px 80px 90px; gap: 10px; padding: 10px 0; border-bottom: 1px solid var(--border); align-items: center; }
  .cat-row:last-child { border-bottom: none; }
  .cat-row.header { padding-bottom: 8px; }
  .cat-th { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); }
  .cat-name { font-size: 13px; font-weight: 600; color: var(--text); }
  .cat-val { font-family: 'JetBrains Mono', monospace; font-size: 12px; color: var(--text-sub); }
  .cat-rev { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: var(--green); }
  .cat-mini-bar { height: 4px; background: var(--border); border-radius: 2px; overflow: hidden; margin-top: 4px; }
  .cat-mini-fill { height: 100%; border-radius: 2px; }
  .leaderboard { display: flex; flex-direction: column; gap: 0; margin-top: 10px; }
  .lb-row { display: grid; grid-template-columns: 32px 1.6fr 90px 80px 110px; gap: 12px; padding: 12px 0; border-bottom: 1px solid var(--border); align-items: center; }
  .lb-row:last-child { border-bottom: none; }
  .lb-row.header { padding-bottom: 8px; }
  .lb-rank { font-family: 'Bebas Neue', sans-serif; font-size: 20px; letter-spacing: 1px; text-align: center; }
  .lb-rank.gold { color: var(--accent); } .lb-rank.silver { color: #94A3B8; } .lb-rank.bronze { color: #CD7F32; } .lb-rank.other { color: var(--text-muted); }
  .lb-advisor { display: flex; align-items: center; gap: 10px; }
  .lb-avatar { width: 30px; height: 30px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; background: var(--purple-dim); color: var(--purple); }
  .lb-name { font-size: 13px; font-weight: 600; color: var(--text); }
  .lb-val { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 600; color: var(--text-sub); }
  .lb-val.amber { color: var(--accent); }
  .lb-tier-pills { display: flex; gap: 4px; }
  .lb-pill { padding: 2px 7px; border-radius: 4px; font-size: 10px; font-weight: 700; }
  .lb-pill.best { background: var(--accent-dim); color: var(--accent); }
  .lb-pill.better { background: var(--blue-dim); color: var(--blue); }
  .lb-pill.good { background: var(--green-dim); color: var(--green); }
  .insight-strip { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-bottom: 18px; }
  .insight-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 16px 18px; display: flex; gap: 14px; align-items: flex-start; }
  .insight-icon { font-size: 22px; flex-shrink: 0; margin-top: 1px; }
  .insight-title { font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 4px; }
  .insight-body { font-size: 12px; color: var(--text-muted); line-height: 1.5; }
  .insight-card.amber { border-color: rgba(245,166,35,0.3); background: rgba(245,166,35,0.04); }
  .insight-card.green { border-color: rgba(34,197,94,0.3); background: rgba(34,197,94,0.04); }
  .insight-card.blue { border-color: rgba(59,130,246,0.3); background: rgba(59,130,246,0.04); }
  .recent-table { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; margin-bottom: 18px; }
  .recent-header { display: grid; grid-template-columns: 1fr 1.2fr 1fr 0.8fr 0.8fr 120px; gap: 12px; padding: 11px 18px; background: var(--surface2); border-bottom: 1px solid var(--border); }
  .recent-row { display: grid; grid-template-columns: 1fr 1.2fr 1fr 0.8fr 0.8fr 120px; gap: 12px; padding: 13px 18px; border-bottom: 1px solid var(--border); align-items: center; font-size: 13px; transition: background 0.12s; }
  .recent-row:last-child { border-bottom: none; }
  .recent-row:hover { background: var(--surface2); }
  .tier-badge { display: inline-flex; padding: 3px 10px; border-radius: 20px; font-size: 10px; font-weight: 700; }
  .tier-badge.best { background: var(--accent-dim); color: var(--accent); border: 1px solid rgba(245,166,35,0.3); }
  .tier-badge.better { background: var(--blue-dim); color: var(--blue); border: 1px solid rgba(59,130,246,0.3); }
  .tier-badge.good { background: var(--green-dim); color: var(--green); border: 1px solid rgba(34,197,94,0.3); }
  .empty-state { text-align: center; padding: 56px 24px; color: var(--text-muted); }
  .empty-icon { font-size: 44px; margin-bottom: 14px; }
  .empty-title { font-family: 'Bebas Neue', sans-serif; font-size: 22px; letter-spacing: 2px; color: var(--text-sub); margin-bottom: 8px; }
  .empty-body { font-size: 13px; color: var(--text-muted); max-width: 400px; margin: 0 auto; line-height: 1.6; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }

  /* LOCKED NAV MODAL */
  .locked-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.75); z-index:9999; display:flex; align-items:center; justify-content:center; }
  .locked-modal { width:min(460px,92vw); background:#161B27; border:1px solid #252D3D; border-radius:16px; overflow:hidden; }
  .locked-modal-top { padding:28px 28px 0; }
  .locked-modal-icon { font-size:36px; margin-bottom:14px; }
  .locked-modal-eyebrow { font-size:10px; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:#F5A623; margin-bottom:8px; }
  .locked-modal-title { font-family:'Bebas Neue',sans-serif; font-size:24px; letter-spacing:2px; color:#F1F5F9; margin-bottom:12px; }
  .locked-modal-body { font-size:13px; color:#94A3B8; line-height:1.7; margin-bottom:20px; }
  .locked-modal-body strong { color:#F1F5F9; }
  .locked-modal-divider { height:1px; background:#252D3D; }
  .locked-modal-footer { padding:18px 28px; display:flex; align-items:center; justify-content:space-between; gap:12px; }
  .locked-modal-note { font-size:11px; color:#475569; line-height:1.5; flex:1; }
  .locked-modal-btn { background:#F5A623; border:none; border-radius:8px; padding:9px 20px; font-size:13px; font-weight:700; color:#000; cursor:pointer; font-family:'DM Sans',sans-serif; white-space:nowrap; transition:opacity 0.15s; }
  .locked-modal-btn:hover { opacity:0.88; }
  .locked-nav-item { display:flex; align-items:center; gap:10px; padding:9px 20px; font-size:13px; font-weight:500; color:#475569; cursor:pointer; transition:all 0.15s; border-left:3px solid transparent; }
  .locked-nav-item:hover { background:#1C2333; color:#64748B; }
  .locked-nav-lock { margin-left:auto; font-size:12px; }
`;

const DATE_RANGES = ["7D", "30D", "90D", "YTD"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const initials = (name) => (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

// Cut-off timestamp for each range filter
function rangeStart(range) {
  const now = new Date();
  if (range === "7D")  { const d = new Date(now); d.setDate(d.getDate() - 7);   return d; }
  if (range === "30D") { const d = new Date(now); d.setDate(d.getDate() - 30);  return d; }
  if (range === "90D") { const d = new Date(now); d.setDate(d.getDate() - 90);  return d; }
  if (range === "YTD") { return new Date(now.getFullYear(), 0, 1); }
  return new Date(0);
}

function Donut({ best, better, good, total }) {
  const r = 52, cx = 65, cy = 65, stroke = 14;
  const circ = 2 * Math.PI * r;
  const gap = 3;
  const bPct = best / 100, btPct = better / 100;
  return (
    <svg width="130" height="130" viewBox="0 0 130 130">
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#252D3D" strokeWidth={stroke} />
      {best > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F5A623" strokeWidth={stroke}
        strokeDasharray={`${circ * bPct - gap} ${circ}`} strokeDashoffset={0}
        transform={`rotate(-90 ${cx} ${cy})`} strokeLinecap="round" />}
      {better > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#3B82F6" strokeWidth={stroke}
        strokeDasharray={`${circ * btPct - gap} ${circ}`} strokeDashoffset={-(circ * bPct)}
        transform={`rotate(-90 ${cx} ${cy})`} strokeLinecap="round" />}
      {good > 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke="#22C55E" strokeWidth={stroke}
        strokeDasharray={`${circ * (good / 100) - gap} ${circ}`} strokeDashoffset={-(circ * (bPct + btPct))}
        transform={`rotate(-90 ${cx} ${cy})`} strokeLinecap="round" />}
    </svg>
  );
}

export default function Reports({ currentUser, onNavigate }) {
  const [range,         setRange]         = useState("30D");
  const [advisorFilter, setAdvisorFilter] = useState("All Advisors");
  const [lookups,       setLookups]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [storeName,     setStoreName]     = useState("");

  // ── Load all lookups for this store ──────────────────────
  useEffect(() => {
    if (currentUser?.storeId) loadLookups();
  }, [currentUser?.storeId]); // eslint-disable-line

  const loadLookups = async () => {
    setLoading(true);
    try {
      const sid = currentUser.storeId;
      // Load store name
      const { getDoc, doc } = await import("firebase/firestore");
      const storeSnap = await getDoc(doc(db, "stores", sid));
      if (storeSnap.exists()) setStoreName(storeSnap.data().name || "");

      // Load all lookups ordered by timestamp desc
      const snap = await getDocs(
        query(collection(db, "stores", sid, "lookups"), orderBy("timestamp", "desc"))
      );
      setLookups(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error("Reports loadLookups:", e);
    }
    setLoading(false);
  };

  // ── Filter by date range ──────────────────────────────────
  const filteredLookups = useMemo(() => {
    const cutoff = rangeStart(range);
    return lookups.filter(l => {
      const ts = l.timestamp?.toDate?.() || new Date(0);
      return ts >= cutoff;
    });
  }, [lookups, range]);

  // ── Compute all stats from real data ──────────────────────
  const stats = useMemo(() => {
    const total = filteredLookups.length;
    if (total === 0) return null;

    // Tier counts
    const tierCounts = { best: 0, better: 0, good: 0 };
    filteredLookups.forEach(l => { if (tierCounts[l.tierSelected] !== undefined) tierCounts[l.tierSelected]++; });
    const bestPct   = Math.round(tierCounts.best   / total * 100);
    const betterPct = Math.round(tierCounts.better / total * 100);
    const goodPct   = Math.round(tierCounts.good   / total * 100);

    // Revenue estimate (sum of selected tier prices)
    const totalRevenue = filteredLookups.reduce((s, l) => s + (l.price || 0), 0);
    const avgPrice     = total > 0 ? Math.round(totalRevenue / total) : 0;

    // Per-advisor stats
    const advisorMap = {};
    filteredLookups.forEach(l => {
      const key = l.advisorId || "unknown";
      if (!advisorMap[key]) advisorMap[key] = { name: l.advisorName || "Unknown", lookups: 0, best: 0, better: 0, good: 0, revenue: 0 };
      advisorMap[key].lookups++;
      if (l.tierSelected) advisorMap[key][l.tierSelected]++;
      advisorMap[key].revenue += (l.price || 0);
    });
    const advisors = Object.values(advisorMap).map(a => ({
      ...a,
      aro:    a.lookups > 0 ? Math.round(a.revenue / a.lookups) : 0,
      bestPct:   a.lookups > 0 ? Math.round(a.best   / a.lookups * 100) : 0,
      betterPct: a.lookups > 0 ? Math.round(a.better / a.lookups * 100) : 0,
      goodPct:   a.lookups > 0 ? Math.round(a.good   / a.lookups * 100) : 0,
    })).sort((a, b) => b.aro - a.aro);

    // Per-category stats
    const catMap = {};
    filteredLookups.forEach(l => {
      const cat = l.category || "other";
      if (!catMap[cat]) catMap[cat] = { name: cat, lookups: 0, revenue: 0 };
      catMap[cat].lookups++;
      catMap[cat].revenue += (l.price || 0);
    });
    const categories = Object.values(catMap).sort((a, b) => b.revenue - a.revenue);
    const maxCatRev  = Math.max(...categories.map(c => c.revenue), 1);

    // Weekly sparkline — last 7 days grouped by day-of-week
    const weekDays = DAYS.map(d => ({ day: d, best: 0, better: 0, good: 0 }));
    const sevenDaysAgo = new Date(); sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
    filteredLookups.forEach(l => {
      const ts = l.timestamp?.toDate?.();
      if (!ts || ts < sevenDaysAgo) return;
      const dayIdx = ts.getDay();
      if (l.tierSelected === "best")   weekDays[dayIdx].best++;
      if (l.tierSelected === "better") weekDays[dayIdx].better++;
      if (l.tierSelected === "good")   weekDays[dayIdx].good++;
    });
    // Rotate so today is last
    const todayIdx = new Date().getDay();
    const rotated = [...weekDays.slice(todayIdx + 1), ...weekDays.slice(0, todayIdx + 1)];
    const maxWeekly = Math.max(...rotated.map(d => d.best + d.better + d.good), 1);

    // Unique VINs
    const uniqueVins = new Set(filteredLookups.map(l => l.vin).filter(Boolean)).size;

    return { total, tierCounts, bestPct, betterPct, goodPct, totalRevenue, avgPrice, advisors, categories, maxCatRev, rotated, maxWeekly, uniqueVins };
  }, [filteredLookups]);

  // ── Recent lookups table (last 20) ────────────────────────
  const recentLookups = filteredLookups.slice(0, 20);

  // ── Advisor names for filter dropdown ─────────────────────
  const advisorNames = useMemo(() => {
    const names = [...new Set(lookups.map(l => l.advisorName).filter(Boolean))];
    return names;
  }, [lookups]);

  const ini = initials(currentUser?.name || "SM");
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [lockedItem,      setLockedItem]      = useState(null);

  const handleLockedNav = (label) => {
    setLockedItem(label);
    setShowLockedModal(true);
  };

  const formatTS = (ts) => {
    const d = ts?.toDate?.();
    if (!d) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const catLabel = (id) => {
    const map = { oil:"Oil Change", brakes:"Brakes", tires:"Tires", battery:"Battery", filters:"Filters", wipers:"Wipers", coolant:"Coolant", transmission:"Transmission" };
    return map[id] || id;
  };

  return (
    <>
      <style>{styles}</style>

      <header className="header">
        <div className="logo">
          <div className="logo-icon">L</div>
          <div className="logo-text">LANE<span>IQ</span></div>
        </div>
        <span className="header-badge">📈 Reports</span>
        <div className="header-right">
          <div className="header-store">
            <strong>{storeName || "Reports"}</strong> &nbsp;·&nbsp; {currentUser?.role === "service_manager" ? "Service Manager" : "Admin"} View
          </div>
          <div className="avatar" title={currentUser?.name}>{ini}</div>
          {onNavigate && (
            <button onClick={() => onNavigate("admin")} style={{ background:"transparent", border:"1px solid var(--border)", borderRadius:6, padding:"6px 12px", fontSize:12, color:"var(--text-muted)", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
              ← Admin
            </button>
          )}
        </div>
      </header>

      <div className="layout">
        <aside className="sidebar">
          <div className="sidebar-label">Reports</div>
          {[
            { icon: "📊", label: "Overview", active: true },
          ].map(item => (
            <div key={item.label} className={`nav-item ${item.active ? "active" : ""}`}>
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </div>
          ))}
          {[
            { icon: "👤", label: "By Advisor"  },
            { icon: "📦", label: "By Category" },
            { icon: "💰", label: "Revenue"     },
          ].map(item => (
            <div key={item.label} className="locked-nav-item" onClick={() => handleLockedNav(item.label)}>
              <span className="nav-icon" style={{opacity:0.5}}>{item.icon}</span>
              <span>{item.label}</span>
              <span className="locked-nav-lock">🔒</span>
            </div>
          ))}
          <div className="sidebar-label">Management</div>
          {[
            { icon: "🔧", label: "Parts Manager", target: "parts" },
            { icon: "⚙️", label: "Admin",          target: "admin" },
          ].map(item => (
            <div key={item.label} className="nav-item" onClick={() => onNavigate?.(item.target)}>
              <span className="nav-icon">{item.icon}</span>
              {item.label}
            </div>
          ))}
        </aside>

        <main className="main">

          {/* FILTERS BAR */}
          <div className="filters-bar">
            <div className="filters-left">
              <span className="page-title">REPORTS</span>
              {DATE_RANGES.map(r => (
                <button key={r} className={`range-pill ${range === r ? "active" : ""}`} onClick={() => setRange(r)}>{r}</button>
              ))}
            </div>
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <select className="advisor-select" value={advisorFilter} onChange={e => setAdvisorFilter(e.target.value)}>
                <option>All Advisors</option>
                {advisorNames.map(n => <option key={n}>{n}</option>)}
              </select>
              <button className="export-btn" onClick={() => window.print()}>⬇ Export</button>
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign:"center", padding:"60px 0", color:"var(--text-muted)", fontSize:13 }}>
              Loading report data...
            </div>
          ) : lookups.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <div className="empty-title">No Data Yet</div>
              <div className="empty-body">
                Reports populate automatically as advisors perform lookups and select tiers in the Advisor View.
                Data appears here in real time — no setup required.
              </div>
            </div>
          ) : !stats ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <div className="empty-title">No Lookups in This Range</div>
              <div className="empty-body">Try a wider date range — there are {lookups.length} total lookups on record.</div>
            </div>
          ) : (
            <>
              {/* KPI STRIP */}
              <div className="kpi-strip">
                <div className="kpi-card purple">
                  <div className="kpi-label">Total Lookups</div>
                  <div className="kpi-value purple">{stats.total}</div>
                  <div className="kpi-sub">in last {range}</div>
                </div>
                <div className="kpi-card amber">
                  <div className="kpi-label">Avg Price / Lookup</div>
                  <div className="kpi-value amber">${stats.avgPrice}</div>
                  <div className="kpi-sub">selected tier avg</div>
                </div>
                <div className="kpi-card green">
                  <div className="kpi-label">Est. Revenue</div>
                  <div className="kpi-value green">${(stats.totalRevenue / 1000).toFixed(1)}k</div>
                  <div className="kpi-sub">sum of selected tiers</div>
                </div>
                <div className="kpi-card blue">
                  <div className="kpi-label">Unique VINs</div>
                  <div className="kpi-value blue">{stats.uniqueVins}</div>
                  <div className="kpi-sub">vehicles looked up</div>
                </div>
                <div className="kpi-card purple">
                  <div className="kpi-label">Best Tier Rate</div>
                  <div className="kpi-value purple">{stats.bestPct}%</div>
                  <div className="kpi-sub">of selections</div>
                </div>
              </div>

              {/* ROW 1: TIER SPLIT + ARO */}
              <div className="chart-grid" style={{ marginBottom:18 }}>
                {/* TIER DONUT */}
                <div className="chart-card">
                  <div style={{ marginBottom:4 }}>
                    <div className="chart-title"><div className="chart-title-bar amber">Tier Selection Split</div></div>
                    <div className="chart-subtitle">Which tier advisors are recommending most</div>
                  </div>
                  <div className="tier-visual">
                    <div className="donut-wrap">
                      <Donut best={stats.bestPct} better={stats.betterPct} good={stats.goodPct} total={stats.total} />
                      <div className="donut-center">
                        <div className="donut-center-val">{stats.total}</div>
                        <div className="donut-center-label">lookups</div>
                      </div>
                    </div>
                    <div className="tier-legend">
                      {[
                        { label:"Best (OEM)",    pct:stats.bestPct,   color:"#F5A623", count:stats.tierCounts.best },
                        { label:"Better",         pct:stats.betterPct, color:"#3B82F6", count:stats.tierCounts.better },
                        { label:"Good",           pct:stats.goodPct,   color:"#22C55E", count:stats.tierCounts.good },
                      ].map(t => (
                        <div key={t.label} className="tier-legend-row">
                          <div className="tier-legend-top">
                            <div className="tier-legend-label">
                              <div className="tier-dot" style={{ background:t.color }} />
                              {t.label}
                              <span style={{ fontSize:11, color:"var(--text-muted)", fontWeight:400 }}>({t.count})</span>
                            </div>
                            <div className="tier-legend-pct" style={{ color:t.color }}>{t.pct}%</div>
                          </div>
                          <div className="tier-bar"><div className="tier-bar-fill" style={{ width:`${t.pct}%`, background:t.color }} /></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ADVISOR ARO BAR */}
                <div className="chart-card">
                  <div style={{ marginBottom:4 }}>
                    <div className="chart-title"><div className="chart-title-bar blue">Advisor Performance</div></div>
                    <div className="chart-subtitle">Average price per lookup by advisor</div>
                  </div>
                  <div className="bar-chart">
                    {stats.advisors.slice(0, 5).map((a, i) => {
                      const maxARO = Math.max(...stats.advisors.map(x => x.aro), 1);
                      return (
                        <div key={a.name} className="bar-row">
                          <div className="bar-label">{a.name.split(" ")[0]}</div>
                          <div className="bar-track">
                            <div className="bar-fill" style={{ width:`${(a.aro/maxARO)*100}%`, background:`hsl(${220 - i*30}, 70%, 55%)` }}>
                              <span className="bar-fill-val">${a.aro}</span>
                            </div>
                          </div>
                          <div className="bar-trend" style={{ color:"var(--text-muted)", fontSize:10 }}>{a.lookups}x</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* WEEKLY SPARKLINE */}
              <div className="chart-card" style={{ marginBottom:18 }}>
                <div style={{ marginBottom:4 }}>
                  <div className="chart-title"><div className="chart-title-bar purple">Weekly Tier Activity</div><span style={{ fontSize:11, color:"var(--text-muted)" }}>Last 7 days</span></div>
                  <div className="chart-subtitle">Daily lookups broken down by tier selected</div>
                </div>
                <div className="sparkline-wrap">
                  <div className="sparkline-days">
                    {stats.rotated.map(d => {
                      const total = d.best + d.better + d.good;
                      const maxH  = 68;
                      return (
                        <div key={d.day} className="spark-col">
                          <div className="spark-val">{total || ""}</div>
                          <div className="spark-bar-wrap" style={{ height:maxH, flexDirection:"column", justifyContent:"flex-end" }}>
                            <div style={{ display:"flex", flexDirection:"column", width:"100%", gap:1 }}>
                              {d.best   > 0 && <div className="spark-bar" style={{ height:`${(d.best   / stats.maxWeekly)*maxH}px`, background:"var(--accent)", opacity:0.9 }} />}
                              {d.better > 0 && <div className="spark-bar" style={{ height:`${(d.better / stats.maxWeekly)*maxH}px`, background:"var(--blue)",   opacity:0.9 }} />}
                              {d.good   > 0 && <div className="spark-bar" style={{ height:`${(d.good   / stats.maxWeekly)*maxH}px`, background:"var(--green)",  opacity:0.9, borderRadius:"0 0 4px 4px" }} />}
                              {total === 0   && <div style={{ height:4, background:"var(--border)", borderRadius:4 }} />}
                            </div>
                          </div>
                          <div className="spark-label">{d.day}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="sparkline-legend">
                    {[["Best (OEM)","var(--accent)"],["Better","var(--blue)"],["Good","var(--green)"]].map(([l,c]) => (
                      <div key={l} className="spark-legend-item">
                        <div className="spark-legend-dot" style={{ background:c }} />{l}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* CATEGORY + LEADERBOARD */}
              <div className="chart-grid" style={{ marginBottom:18 }}>
                {/* CATEGORY TABLE */}
                <div className="chart-card">
                  <div style={{ marginBottom:12 }}>
                    <div className="chart-title"><div className="chart-title-bar green">Revenue by Category</div></div>
                    <div className="chart-subtitle">Which repair types are driving the most activity</div>
                  </div>
                  <div className="cat-table">
                    <div className="cat-row header">
                      <div className="cat-th">Category</div>
                      <div className="cat-th">Lookups</div>
                      <div className="cat-th">Revenue</div>
                      <div className="cat-th">Share</div>
                    </div>
                    {stats.categories.slice(0, 7).map(c => (
                      <div key={c.name} className="cat-row">
                        <div>
                          <div className="cat-name">{catLabel(c.name)}</div>
                          <div className="cat-mini-bar">
                            <div className="cat-mini-fill" style={{ width:`${(c.revenue/stats.maxCatRev)*100}%`, background:"var(--green)" }} />
                          </div>
                        </div>
                        <div className="cat-val">{c.lookups}</div>
                        <div className="cat-rev">${c.revenue >= 1000 ? (c.revenue/1000).toFixed(1)+"k" : c.revenue.toFixed(0)}</div>
                        <div className="cat-val">{Math.round(c.lookups/stats.total*100)}%</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ADVISOR LEADERBOARD */}
                <div className="chart-card">
                  <div style={{ marginBottom:12 }}>
                    <div className="chart-title"><div className="chart-title-bar purple">Advisor Leaderboard</div></div>
                    <div className="chart-subtitle">Ranked by avg price per lookup — {range}</div>
                  </div>
                  <div className="leaderboard">
                    <div className="lb-row header">
                      <div className="cat-th">#</div>
                      <div className="cat-th">Advisor</div>
                      <div className="cat-th">Avg $</div>
                      <div className="cat-th">Lookups</div>
                      <div className="cat-th">Tiers</div>
                    </div>
                    {(advisorFilter === "All Advisors" ? stats.advisors : stats.advisors.filter(a => a.name === advisorFilter))
                      .slice(0, 6)
                      .map((a, i) => (
                        <div key={a.name} className="lb-row">
                          <div className={`lb-rank ${i===0?"gold":i===1?"silver":i===2?"bronze":"other"}`}>{i+1}</div>
                          <div className="lb-advisor">
                            <div className="lb-avatar">{initials(a.name)}</div>
                            <div className="lb-name">{a.name}</div>
                          </div>
                          <div className="lb-val amber">${a.aro}</div>
                          <div className="lb-val">{a.lookups}</div>
                          <div className="lb-tier-pills">
                            <span className="lb-pill best">{a.bestPct}%</span>
                            <span className="lb-pill better">{a.betterPct}%</span>
                            <span className="lb-pill good">{a.goodPct}%</span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>

              {/* RECENT LOOKUPS TABLE */}
              <div style={{ marginBottom:4 }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:"1.5px", textTransform:"uppercase", color:"var(--text-muted)", marginBottom:12, display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ display:"block", width:3, height:12, background:"var(--purple)", borderRadius:2 }} />
                  Recent Lookups
                </div>
              </div>
              <div className="recent-table">
                <div className="recent-header">
                  <div className="cat-th">Vehicle</div>
                  <div className="cat-th">Part / Category</div>
                  <div className="cat-th">Advisor</div>
                  <div className="cat-th">Tier</div>
                  <div className="cat-th">Price</div>
                  <div className="cat-th">Time</div>
                </div>
                {recentLookups.map(l => (
                  <div key={l.id} className="recent-row">
                    <div style={{ fontSize:13, fontWeight:600 }}>
                      {l.year} {l.make} {l.model}
                      <div style={{ fontSize:10, fontFamily:"'JetBrains Mono',monospace", color:"var(--text-muted)", marginTop:2 }}>{l.vin?.slice(-6)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:12, fontWeight:600, color:"var(--text-sub)" }}>{catLabel(l.category)}</div>
                      {l.partName && <div style={{ fontSize:11, color:"var(--text-muted)" }}>{l.partName}</div>}
                    </div>
                    <div style={{ fontSize:12, color:"var(--text-sub)" }}>{l.advisorName || "—"}</div>
                    <div><span className={`tier-badge ${l.tierSelected}`}>{l.tierSelected?.toUpperCase()}</span></div>
                    <div style={{ fontFamily:"'JetBrains Mono',monospace", fontSize:13, fontWeight:700, color:"var(--green)" }}>
                      {l.price ? `$${l.price.toFixed(2)}` : "—"}
                    </div>
                    <div style={{ fontSize:11, color:"var(--text-muted)" }}>{formatTS(l.timestamp)}</div>
                  </div>
                ))}
              </div>
            </>
          )}

        </main>
      </div>

      {/* LOCKED FEATURE MODAL */}
      {showLockedModal && (
        <div className="locked-overlay" onClick={() => setShowLockedModal(false)}>
          <div className="locked-modal" onClick={e => e.stopPropagation()}>
            <div className="locked-modal-top">
              <div className="locked-modal-icon">📊</div>
              <div className="locked-modal-eyebrow">Personalized Reporting</div>
              <div className="locked-modal-title">{lockedItem} View</div>
              <div className="locked-modal-body">
                <strong>These reports are built around your store's data.</strong>
                <br /><br />
                The {lockedItem} dashboard pulls directly from your team's real lookup history —
                your advisors, your vehicles, your repair categories. In a live store, this view
                populates automatically from day one with zero setup required.
                <br /><br />
                The Overview dashboard above gives you a real-time preview of exactly what
                your data will look like once your store is live.
              </div>
            </div>
            <div className="locked-modal-divider" />
            <div className="locked-modal-footer">
              <div className="locked-modal-note">
                All reporting views activate automatically when your store goes live.
                No configuration needed.
              </div>
              <button className="locked-modal-btn" onClick={() => setShowLockedModal(false)}>
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
